import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import AdminShell from "@/components/AdminShell";
import { Eyebrow } from "@/components/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ALLOWED = ['mp4', 'mov', 'avi', 'mp3', 'wav', 'm4a', 'pdf', 'txt', 'md', 'xlsx', 'xls'];
const stripExt = (name) => name ? name.replace(/\.[^/.]+$/, '') : name;
const KB = 1024;
const MB = KB * KB;

const YouTubeIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.5 6.19a3 3 0 00-2.11-2.12C19.47 3.5 12 3.5 12 3.5s-7.47 0-9.39.57A3 3 0 00.5 6.19 31.4 31.4 0 000 12a31.4 31.4 0 00.5 5.81 3 3 0 002.11 2.12C4.53 20.5 12 20.5 12 20.5s7.47 0 9.39-.57a3 3 0 002.11-2.12A31.4 31.4 0 0024 12a31.4 31.4 0 00-.5-5.81zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/>
  </svg>
);

export default function UploadPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("upload"); // upload | gdrive | youtube
  const [viewingTranscript, setViewingTranscript] = useState(null);

  // Multi-file upload state
  const [uploadQueue, setUploadQueue] = useState([]); // [{file, status, progress}]
  const [uploading, setUploading] = useState(false);

  // Search/filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, txt, pdf, mp4, xlsx, srt, docx
  const [filterStatus, setFilterStatus] = useState("all"); // all, completed, processing, failed

  // Google Drive state
  const [gdriveUrl, setGdriveUrl] = useState("");
  const [gdriveTitle, setGdriveTitle] = useState("");
  const [gdriveLoading, setGdriveLoading] = useState(false);

  const isFolderUrl = gdriveUrl.includes("/folders/");

  // Bulk YouTube state
  const [urlMap, setUrlMap] = useState({}); // { doc_id: youtube_url }
  const [savingBulk, setSavingBulk] = useState(false);
  const [bulkFilter, setBulkFilter] = useState("");

  // Applicant-code tags for existing documents { doc_id: code }
  const [codeMap, setCodeMap] = useState({});

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/documents`, { headers: { Authorization: `Bearer ${token}` } });
      const docs = res.data.documents;
      setDocuments(docs);
      setUrlMap(prev => {
        const next = { ...prev };
        docs.forEach(d => {
          if (d.youtube_url && !next[d.id]) next[d.id] = d.youtube_url;
        });
        return next;
      });
      setCodeMap(prev => {
        const next = { ...prev };
        docs.forEach(d => {
          if (next[d.id] === undefined) next[d.id] = d.applicant_code || "";
        });
        return next;
      });
    } catch (_e) {
      // Silent fail — document list will just show empty state
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDocuments();
    const interval = setInterval(fetchDocuments, 3000);
    return () => clearInterval(interval);
  }, [fetchDocuments]);

  // ── Multi-file upload ──────────────────────────────────────────────
  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files);
    const valid = files.filter(f => ALLOWED.includes(f.name.split('.').pop().toLowerCase()));
    const invalid = files.length - valid.length;
    if (invalid > 0) toast.warning(`${invalid} file(s) skipped — unsupported type`);
    if (!valid.length) return;
    setUploadQueue(valid.map(f => ({ file: f, status: "queued", applicantCode: "" })));
    e.target.value = null;
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const valid = files.filter(f => ALLOWED.includes(f.name.split('.').pop().toLowerCase()));
    if (!valid.length) { toast.error("No supported files found"); return; }
    setUploadQueue(valid.map(f => ({ file: f, status: "queued", applicantCode: "" })));
  }, []);

  const startUpload = async () => {
    if (!uploadQueue.length || uploading) return;
    setUploading(true);

    for (let i = 0; i < uploadQueue.length; i++) {
      setUploadQueue(q => q.map((item, idx) => idx === i ? { ...item, status: "uploading", progress: 0 } : item));
      const formData = new FormData();
      formData.append("file", uploadQueue[i].file);
      const code = (uploadQueue[i].applicantCode || "").trim();
      if (code) formData.append("applicant_code", code);
      try {
        await axios.post(`${API}/documents/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            setUploadQueue(q => q.map((item, idx) => idx === i ? { ...item, progress: pct } : item));
          }
        });
        setUploadQueue(q => q.map((item, idx) => idx === i ? { ...item, status: "done", progress: 100 } : item));
      } catch (err) {
        setUploadQueue(q => q.map((item, idx) => idx === i ? { ...item, status: "error" } : item));
      }
    }
    setUploading(false);
    fetchDocuments();
    toast.success("All files uploaded!");
  };

  const clearQueue = () => setUploadQueue([]);

  const saveApplicantCode = async (docId) => {
    try {
      await axios.put(`${API}/documents/${docId}/applicant-code`,
        { applicant_code: (codeMap[docId] || "").trim() || null },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Applicant tag saved");
      fetchDocuments();
    } catch (_e) {
      toast.error("Failed to save applicant tag");
    }
  };

  // Google Drive
  const handleGDriveImport = async (e) => {
    e.preventDefault();
    if (!gdriveUrl.trim()) { toast.error("Please paste a Google Drive link"); return; }
    setGdriveLoading(true);
    try {
      if (isFolderUrl) {
        await axios.post(`${API}/documents/import-gdrive-folder`,
          { folder_url: gdriveUrl.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Folder import started! All files will appear as they are processed.");
      } else {
        await axios.post(`${API}/documents/import-gdrive`,
          { gdrive_url: gdriveUrl.trim(), title: gdriveTitle.trim() || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("File import started!");
      }
      setGdriveUrl(""); setGdriveTitle("");
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setGdriveLoading(false);
    }
  };

  // ── Bulk YouTube ───────────────────────────────────────────────────
  const handleBulkSave = async () => {
    const updates = Object.entries(urlMap)
      .map(([doc_id, youtube_url]) => ({ doc_id, youtube_url: youtube_url || null }));
    if (!updates.length) return;
    setSavingBulk(true);
    try {
      await axios.put(`${API}/documents/bulk-video-urls`,
        { updates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("All YouTube links saved!");
      fetchDocuments();
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSavingBulk(false);
    }
  };

  const completedDocs = useMemo(() => documents.filter(d => d.status === "completed"), [documents]);
  const filteredDocs = useMemo(() => completedDocs.filter(d =>
    !bulkFilter || d.filename.toLowerCase().includes(bulkFilter.toLowerCase())
  ), [completedDocs, bulkFilter]);
  const linkedCount = useMemo(() => completedDocs.filter(d => urlMap[d.id]?.trim()).length, [completedDocs, urlMap]);

  // ── Helpers ────────────────────────────────────────────────────────
  const getStatusColor = (s) => ({
    completed: "bg-mint text-equal-dark border-[#bfe5d1]",
    processing: "bg-[#e8eef7] text-[#1f5f8b] border-[#c9d8ea]",
    failed: "bg-[#fdecea] text-[#c0392b] border-[#f2c1bb]"
  }[s] || "bg-page text-mute border-line");

  const queueStatusIcon = (s) => ({
    queued: <span className="text-mute">–</span>,
    uploading: <span className="animate-spin inline-block w-4 h-4 border-2 border-equal border-t-transparent rounded-full"></span>,
    done: <span className="text-equal-dark font-bold">✓</span>,
    error: <span className="text-[#c0392b] font-bold">✗</span>
  }[s]);

  const tabs = [
    { id: "upload", label: "Upload Files" },
    { id: "gdrive", label: "Google Drive" },
    { id: "youtube", label: `Link YouTube ${linkedCount > 0 ? `(${linkedCount}/${completedDocs.length})` : ""}` }
  ];

  return (
    <AdminShell active="upload">
      {/* Page header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="back-to-dashboard">← Back</Button>
      </div>
      <div className="mb-10">
        <Eyebrow className="mb-3">Admin</Eyebrow>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-ink tracking-tight leading-[1.05]">Knowledge Base</h1>
      </div>

      <div className="space-y-6 max-w-5xl">

        {/* Tabs + Content */}
        <section className="card-flat">
          <div className="p-6 border-b border-line">
            <h2 className="text-lg font-bold text-ink tracking-tight">Add to Knowledge Base</h2>
            <div className="flex gap-2 flex-wrap mt-4">
              {tabs.map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium border transition-colors ${activeTab === t.id ? "bg-ink text-white border-ink" : "bg-white text-ink-2 border-line hover:bg-page"}`}
                  data-testid={`tab-${t.id}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* ── Upload Files ── */}
            {activeTab === "upload" && (
              <div className="space-y-4">
                <div
                  className="border-2 border-dashed border-line rounded-xl p-10 text-center hover:border-equal hover:bg-mint/40 transition-colors cursor-pointer"
                  onDrop={handleDrop}
                  onDragOver={e => e.preventDefault()}
                  onClick={() => !uploadQueue.length && document.getElementById("file-input").click()}
                >
                  <input id="file-input" type="file" className="hidden" multiple
                    accept=".mp4,.mov,.avi,.mp3,.wav,.m4a,.pdf,.txt,.md,.xlsx,.xls"
                    onChange={handleFilesSelected} data-testid="file-input" />
                  <div className="w-14 h-14 rounded-xl bg-mint border border-[#bfe5d1] flex items-center justify-center mx-auto mb-4">
                    <svg className="w-7 h-7 text-equal-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <p className="text-base font-semibold text-ink mb-1">Click or drag & drop — select multiple files at once</p>
                  <p className="text-sm text-mute">TXT, PDF, XLSX, MD · MP4, MOV, MP3, WAV, M4A</p>
                  <p className="text-xs text-mute mt-1">Large videos are automatically split into chunks for transcription</p>
                </div>

                {uploadQueue.length > 0 && (
                  <div className="card-flat overflow-hidden">
                    <div className="bg-page px-4 py-3 flex items-center justify-between border-b border-line">
                      <div>
                        <span className="font-semibold text-ink text-sm">{uploadQueue.length} file{uploadQueue.length > 1 ? "s" : ""} selected</span>
                        <p className="text-xs text-mute mt-0.5">Add an anonymized applicant code (e.g. A, B) to any session file so the Monthly Report can count active applicants. Optional.</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={clearQueue} disabled={uploading}>Clear</Button>
                        <Button size="sm"
                          onClick={startUpload} disabled={uploading} data-testid="start-upload-button">
                          {uploading ? "Uploading..." : `Upload All ${uploadQueue.length} Files`}
                        </Button>
                      </div>
                    </div>
                    <div className="divide-y divide-line max-h-64 overflow-y-auto">
                      {uploadQueue.map((item, idx) => (
                        <div key={`${item.file.name}-${idx}`} className="flex items-center justify-between px-4 py-3">
                          <span className="text-sm text-ink truncate flex-1 mr-4">{item.file.name}</span>
                          <div className="flex items-center gap-3">
                            <input
                              type="text"
                              placeholder="Applicant code"
                              value={item.applicantCode || ""}
                              onChange={e => setUploadQueue(q => q.map((it, i2) => i2 === idx ? { ...it, applicantCode: e.target.value } : it))}
                              disabled={uploading}
                              className="input-flat h-8 text-xs w-32"
                              title="Anonymized applicant code, e.g. A (optional)"
                              data-testid={`queue-applicant-code-${idx}`}
                            />
                            <span className="text-xs text-mute font-mono">{(item.file.size / MB).toFixed(1)} MB</span>
                            {item.status === "uploading" && (
                              <div className="flex items-center gap-2">
                                <div className="w-20 h-1.5 bg-line rounded-full overflow-hidden">
                                  <div className="h-full bg-equal rounded-full transition-[width] duration-300"
                                    style={{ width: `${item.progress || 0}%` }} />
                                </div>
                                <span className="text-xs text-equal-dark font-mono w-8">{item.progress || 0}%</span>
                              </div>
                            )}
                            {item.status !== "uploading" && queueStatusIcon(item.status)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Google Drive ── */}
            {activeTab === "gdrive" && (
              <form onSubmit={handleGDriveImport} className="space-y-4">
                <div className="bg-cream border border-[#e6dfd0] rounded-xl p-4 text-sm text-ink-2">
                  <p className="font-semibold text-ink mb-1">Import a folder or a single file:</p>
                  <ol className="list-decimal pl-4 space-y-1">
                    <li>Open Google Drive and find your folder (or file)</li>
                    <li>Right-click → <strong>Share</strong> → set to <strong>"Anyone with the link"</strong> → Save</li>
                    <li>Click <strong>Copy link</strong> and paste below</li>
                  </ol>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-ink mb-1">
                    Google Drive Link *
                  </label>
                  <Input type="url"
                    placeholder="https://drive.google.com/drive/folders/... or /file/d/..."
                    value={gdriveUrl} onChange={e => setGdriveUrl(e.target.value)}
                    required className="h-11" data-testid="gdrive-url-input" />
                </div>

                {/* Auto-detect folder vs file */}
                {gdriveUrl && (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-[10px] text-sm font-medium border ${isFolderUrl ? "bg-mint text-equal-dark border-[#bfe5d1]" : "bg-[#e8eef7] text-[#1f5f8b] border-[#c9d8ea]"}`}>
                    {isFolderUrl ? (
                      <>
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                        Folder detected — all .txt, .pdf, .xlsx files inside will be imported automatically
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Single file detected
                      </>
                    )}
                  </div>
                )}

                {/* Title only needed for single file */}
                {!isFolderUrl && (
                  <div>
                    <label className="block text-sm font-semibold text-ink mb-1">Title (optional)</label>
                    <Input type="text" placeholder="e.g. Workshop Session 1 - Jan 2025"
                      value={gdriveTitle} onChange={e => setGdriveTitle(e.target.value)}
                      className="h-11" data-testid="gdrive-title-input" />
                  </div>
                )}

                <Button type="submit" className="w-full h-11"
                  disabled={gdriveLoading} data-testid="gdrive-import-button">
                  {gdriveLoading
                    ? "Starting import..."
                    : isFolderUrl ? "Import Entire Folder" : "Import File"}
                </Button>
              </form>
            )}

            {/* ── Bulk YouTube Linker ── */}
            {activeTab === "youtube" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <Input placeholder="Filter by filename..." value={bulkFilter}
                      onChange={e => setBulkFilter(e.target.value)} className="h-9" />
                  </div>
                  <div className="text-sm text-mute whitespace-nowrap font-mono">
                    {linkedCount} / {completedDocs.length} linked
                  </div>
                  <Button className="h-9 px-6"
                    onClick={handleBulkSave} disabled={savingBulk} data-testid="save-all-youtube-button">
                    {savingBulk ? "Saving..." : "Save All"}
                  </Button>
                </div>

                {completedDocs.length === 0 ? (
                  <div className="text-center py-12 text-mute">
                    <p className="text-base font-semibold text-ink">No completed documents yet</p>
                    <p className="text-sm mt-1">Upload your transcript files first, then come back here to add YouTube links</p>
                  </div>
                ) : (
                  <div className="card-flat overflow-hidden">
                    {/* Column headers */}
                    <div className="grid grid-cols-2 gap-4 px-4 py-2.5 border-b border-line th-mono">
                      <span>Document</span>
                      <span>YouTube URL</span>
                    </div>
                    <div className="divide-y divide-line max-h-[500px] overflow-y-auto">
                      {filteredDocs.map(doc => (
                        <div key={doc.id} className="grid grid-cols-2 gap-4 px-4 py-3 items-center hover:bg-page transition-colors"
                          data-testid={`bulk-row-${doc.id}`}>
                          <div>
                            <p className="text-sm font-medium text-ink truncate" title={doc.filename}>
                              {stripExt(doc.filename)}
                            </p>
                            <p className="text-xs text-mute mt-0.5 font-mono">
                              {doc.file_type?.toUpperCase()} · {new Date(doc.uploaded_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              placeholder="https://youtu.be/..."
                              value={urlMap[doc.id] || ""}
                              onChange={e => setUrlMap(prev => ({ ...prev, [doc.id]: e.target.value }))}
                              className={`h-8 text-sm ${urlMap[doc.id] ? "border-equal bg-mint/50" : ""}`}
                              data-testid={`youtube-input-${doc.id}`}
                            />
                            {urlMap[doc.id] && (
                              <a href={urlMap[doc.id]} target="_blank" rel="noopener noreferrer"
                                className="flex-shrink-0 text-[#c0392b] hover:text-ink" title="Preview">
                                <YouTubeIcon />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-xs text-mute text-center">
                  Paste YouTube URLs in the fields above — rows turn green when filled. Click <strong>Save All</strong> when done.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Documents List */}
        <section className="card-flat">
          <div className="p-6 border-b border-line">
            <h2 className="text-lg font-bold text-ink tracking-tight">
              Knowledge Base
              <span className="ml-2 text-sm font-normal text-mute font-mono">({documents.length} documents)</span>
            </h2>
            {/* Search + Filter */}
            {documents.length > 0 && (
              <div className="flex gap-3 mt-4 flex-wrap">
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="input-flat h-9 flex-1 min-w-[160px]"
                  data-testid="document-search"
                />
                <select value={filterType} onChange={e => setFilterType(e.target.value)}
                  className="input-flat h-9 w-auto text-xs text-ink-2"
                  data-testid="document-filter-type">
                  <option value="all">All types</option>
                  <option value="txt">TXT</option>
                  <option value="pdf">PDF</option>
                  <option value="mp4">Video</option>
                  <option value="xlsx">Excel</option>
                  <option value="srt">SRT</option>
                  <option value="docx">Word</option>
                </select>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                  className="input-flat h-9 w-auto text-xs text-ink-2"
                  data-testid="document-filter-status">
                  <option value="all">All status</option>
                  <option value="completed">Completed</option>
                  <option value="processing">Processing</option>
                  <option value="failed">Failed</option>
                </select>
                {(searchTerm || filterType !== "all" || filterStatus !== "all") && (
                  <button onClick={() => { setSearchTerm(""); setFilterType("all"); setFilterStatus("all"); }}
                    className="btn-ghost h-9 px-3 text-xs">
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="p-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-line border-t-equal"></div>
              </div>
            ) : documents.length === 0 ? (
              <p className="text-center py-10 text-mute" data-testid="no-documents-message">
                No documents yet — upload your first file above.
              </p>
            ) : (
              <ScrollArea className="h-96">
                <div className="space-y-2">
                  {documents
                    .filter(doc => {
                      const matchSearch = !searchTerm || doc.filename.toLowerCase().includes(searchTerm.toLowerCase());
                      const matchType = filterType === "all" || doc.file_type === filterType || (filterType === "mp4" && ["mp4","mov","avi","mp3","wav","m4a"].includes(doc.file_type));
                      const matchStatus = filterStatus === "all" || doc.status === filterStatus;
                      return matchSearch && matchType && matchStatus;
                    })
                    .map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 border border-line rounded-[10px] hover:bg-page transition-colors"
                      data-testid={`document-${doc.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-ink text-sm truncate">{stripExt(doc.filename)}</p>
                          {doc.youtube_url && (
                            <a href={doc.youtube_url} target="_blank" rel="noopener noreferrer"
                              className="flex-shrink-0 text-[#c0392b] hover:text-ink" title="YouTube linked">
                              <YouTubeIcon className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                        <p className="text-xs text-mute font-mono">{doc.file_type?.toUpperCase()} · {new Date(doc.uploaded_at).toLocaleString()}</p>
                        {doc.status === "processing" && doc.status_detail && (
                          <p className="text-xs text-[#1f5f8b] mt-0.5 flex items-center gap-1">
                            <span className="animate-spin inline-block w-3 h-3 border-2 border-[#1f5f8b] border-t-transparent rounded-full"></span>
                            {doc.status_detail}
                          </p>
                        )}
                        {doc.status === "failed" && doc.status_detail && (
                          <p className="text-xs text-[#c0392b] mt-0.5 truncate">{doc.status_detail}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                        {doc.status === "completed" && doc.filename.toLowerCase().includes("session") && (
                          <input
                            type="text"
                            placeholder="Applicant"
                            value={codeMap[doc.id] ?? ""}
                            onChange={e => setCodeMap(prev => ({ ...prev, [doc.id]: e.target.value }))}
                            onBlur={() => saveApplicantCode(doc.id)}
                            onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                            className="input-flat h-7 text-xs w-24"
                            title="Anonymized applicant code (e.g. A) — used by the Monthly Report"
                            data-testid={`applicant-code-${doc.id}`}
                          />
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-md font-medium border ${getStatusColor(doc.status)}`}
                          data-testid={`status-${doc.id}`}>
                          {doc.status === "completed" && "✓ Done"}
                          {doc.status === "processing" && "⏳ Processing"}
                          {doc.status === "failed" && "✗ Failed"}
                        </span>
                        {doc.status === "completed" && (
                          <Button variant="outline" size="sm" className="h-7 text-xs"
                            onClick={() => setViewingTranscript(doc)} data-testid={`view-${doc.id}`}>
                            View
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-[#c0392b] hover:bg-[#fdecea] hover:text-[#c0392b]"
                          onClick={async () => {
                            if (!window.confirm("Delete this document?")) return;
                            await axios.delete(`${API}/documents/${doc.id}`, { headers: { Authorization: `Bearer ${token}` } });
                            fetchDocuments();
                          }} data-testid={`delete-${doc.id}`}>
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </section>
      </div>

      {/* Transcript Modal */}
      <Dialog open={!!viewingTranscript} onOpenChange={() => setViewingTranscript(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-ink">{stripExt(viewingTranscript?.filename)}</DialogTitle>
            <DialogDescription className="font-mono text-xs">{viewingTranscript?.chunk_count || 0} chunks · {viewingTranscript?.file_type?.toUpperCase()}</DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] mt-4">
            <p className="text-sm text-ink-2 whitespace-pre-wrap leading-relaxed px-1">
              {viewingTranscript?.transcript_text || "No transcript available"}
            </p>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
